import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Autocomplete,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useNavigate, useParams } from "react-router-dom";

import PageBanner from "../components/PageBanner";
import { AppConfirmDialog, AppToast } from "../components/AppFeedback";
import DatagridNavLink from "../components/DatagridNavLink";
import {
  addProjectTimelinesBulk,
  addProjectUser,
  deleteProjectUser,
  deleteProjectDocument,
  fetchExecutionSchedules,
  fetchExecutions,
  fetchProjectById,
  fetchProjectDocuments,
  fetchProjectTimelines,
  getProjectDocumentDownloadUrl,
  updateProject,
  updateProjectTimeline,
  uploadProjectDocument,
} from "../services/projectService";
import { useUserContext } from "../context/UserContext";
import { fetchKeycloakUsersDropdown } from "../services/keycloakUserService";
import { fetchQuestionnairesForSelect } from "../services/questionnaireService";
import { formatDate } from "../utils/dateFormat";
import PatientCreateDialog from "./patient-management/PatientCreateDialog";

function toTitleCase(value = "") {
  const v = String(value || "").trim();
  if (!v) return "";
  return v.charAt(0).toUpperCase() + v.slice(1).toLowerCase();
}

function addDaysToIsoDate(isoDate, days) {
  if (!isoDate) return "";
  const dt = new Date(isoDate);
  if (Number.isNaN(dt.getTime())) return "";
  dt.setDate(dt.getDate() + Number(days || 0));
  return dt.toISOString().slice(0, 10);
}

function formatBytes(bytes) {
  if (!bytes || Number(bytes) <= 0) return "0 B";
  const value = Number(bytes);
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
}

function toErrorMessage(error, fallbackMessage) {
  const raw = error?.message || "";

  try {
    const parsed = JSON.parse(raw);
    const detail = parsed?.detail;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) {
      return detail
        .map((item) => {
          if (!item || typeof item !== "object") return String(item);
          const loc = Array.isArray(item.loc) ? item.loc.join(".") : "";
          const msg = item.msg || JSON.stringify(item);
          return loc ? `${loc}: ${msg}` : msg;
        })
        .join("; ");
    }
  } catch {
    // ignore
  }

  return String(raw || fallbackMessage);
}

const doctypeOptions = [
  { value: "Agreement", label: "Agreement" },
  { value: "IRB", label: "IRB" },
  { value: "Official", label: "Official" },
  { value: "Budget", label: "Budget" },
  { value: "Others", label: "Others" },
];

const emptyUploadForm = { doctype: "Agreement", customDoctype: "", document_name: "", file: null };

const ProjectDetail = () => {
  const navigate = useNavigate();
  const { proid } = useParams();
  const { roles = [], username = "" } = useUserContext();
  const isAdmin = roles.some((role) => String(role).toUpperCase() === "ADMIN");
  const isManager = roles.some((role) => String(role).toUpperCase() === "MANAGER");
  const canRelaxTimelineDateBounds = isAdmin || isManager;

  const projectId = Number(proid);

  const [loading, setLoading] = useState(true);
  const [savingProject, setSavingProject] = useState(false);
  const [project, setProject] = useState(null);
  const [projectForm, setProjectForm] = useState({
    name: "",
    status: "pending",
    type: "other",
    number_of_visits: 1,
    start_date: "",
    end_date: "",
  });
  const [initialProjectForm, setInitialProjectForm] = useState(null);
  const [initialNumberOfVisits, setInitialNumberOfVisits] = useState(null);
  const [projectPeople, setProjectPeople] = useState([]);
  const [initialProjectPeople, setInitialProjectPeople] = useState([]);
  const [keycloakUsers, setKeycloakUsers] = useState([]);
  const [loadingProjectPeople, setLoadingProjectPeople] = useState(false);
  const [timelineRows, setTimelineRows] = useState([]);
  const [documentRows, setDocumentRows] = useState([]);
  const [subjectRows, setSubjectRows] = useState([]);

  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [timelinesLoading, setTimelinesLoading] = useState(false);
  const [subjectsLoading, setSubjectsLoading] = useState(false);

  const [timelineModalOpen, setTimelineModalOpen] = useState(false);
  const [timelineSaving, setTimelineSaving] = useState(false);
  const [selectedTimeline, setSelectedTimeline] = useState(null);
  const [timelineForm, setTimelineForm] = useState({
    type: "static",
    date: "",
    tolerance: 0,
    offset: 0,
    questionnaireQids: [],
  });
  const [questionnaireOptions, setQuestionnaireOptions] = useState([]);
  const [loadingQuestionnaires, setLoadingQuestionnaires] = useState(false);
  const [incompletePromptOpen, setIncompletePromptOpen] = useState(false);
  const [incompletePromptSeen, setIncompletePromptSeen] = useState(false);
  const [addSchedulesOpen, setAddSchedulesOpen] = useState(false);
  const [addingSchedules, setAddingSchedules] = useState(false);
  const [incompleteScheduleRows, setIncompleteScheduleRows] = useState([]);
  const [scheduleChangePrompt, setScheduleChangePrompt] = useState({
    open: false,
    mode: null, // currently only "decrease"
  });

  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadForm, setUploadForm] = useState(emptyUploadForm);
  const [deleteDocumentId, setDeleteDocumentId] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [createPatientOpen, setCreatePatientOpen] = useState(false);

  const loadProjectCore = async () => {
    const data = await fetchProjectById(projectId);
    setProject(data || null);
    const formState = {
      name: data?.name || "",
      status: (data?.status || "pending").toLowerCase(),
      type: (data?.type || "other").toLowerCase(),
      number_of_visits: data?.number_of_visits ?? 1,
      start_date: data?.start_date || "",
      end_date: data?.end_date || "",
    };
    setProjectForm(formState);
    setInitialProjectForm(formState);
    setInitialNumberOfVisits(
      data && typeof data.number_of_visits === "number"
        ? data.number_of_visits
        : null
    );
  };

  const loadProjectPeople = async () => {
    setLoadingProjectPeople(true);
    try {
      const [projectDataResult, usersDataResult] = await Promise.allSettled([
        fetchProjectById(projectId),
        fetchKeycloakUsersDropdown({ limit: 500 }),
      ]);
      const projectData = projectDataResult.status === "fulfilled" ? projectDataResult.value : null;
      const usersData = usersDataResult.status === "fulfilled" ? usersDataResult.value : [];
      const assignedUsernames = (projectData?.project_usernames || []).filter(Boolean);

      const userMap = new Map((usersData || []).map((u) => [u.username, u]));
      const selected = assignedUsernames.map((username) => userMap.get(username) || { username, name: username });
      setKeycloakUsers(usersData || []);
      setProjectPeople(selected);
      setInitialProjectPeople(selected);
    } finally {
      setLoadingProjectPeople(false);
    }
  };

  const loadTimelines = async () => {
    setTimelinesLoading(true);
    try {
      const rows = await fetchProjectTimelines(projectId);
      setTimelineRows(
        (rows || []).map((row) => ({
          ...row,
          id: row.id,
          tolerance: Number(row.tolerance ?? 0),
          offset:
            String(row.type || "").toLowerCase() === "dynamic"
              ? Number(row.offset ?? 1)
              : Number(row.offset ?? 0),
        }))
      );
    } finally {
      setTimelinesLoading(false);
    }
  };

  const loadDocuments = async () => {
    setDocumentsLoading(true);
    try {
      const rows = await fetchProjectDocuments(projectId);
      setDocumentRows(
        (rows || []).map((row) => ({
          ...row,
          id: row.id,
        }))
      );
    } finally {
      setDocumentsLoading(false);
    }
  };

  const loadSubjects = async () => {
    setSubjectsLoading(true);
    try {
      let page = 1;
      const limit = 100;
      let totalPages = 1;
      let all = [];

      while (page <= totalPages) {
        // eslint-disable-next-line no-await-in-loop
        const batch = await fetchExecutions({ page, limit });
        const results = batch?.results || [];
        all = [...all, ...results];
        totalPages = Number(batch?.pagination?.total_pages || 1);
        page += 1;
      }

      const filtered = all.filter((row) => Number(row.project_id) === projectId);

      const withNext = await Promise.all(
        filtered.map(async (row) => {
          try {
            const schedules = await fetchExecutionSchedules(row.id, { page: 1, limit: 200 });
            const list = schedules?.results || [];
            const nextPending = list.find((s) => String(s.status || "").toLowerCase() !== "completed");
            const nextSchedule = nextPending
              ? nextPending.actual_date
                ? formatDate(nextPending.actual_date)
                : `Order ${nextPending.order}`
              : "-";

            return {
              id: row.id,
              patient_id: row.patient?.id,
              patient_name: row.patient?.name || [row.patient?.first_name, row.patient?.middle_name, row.patient?.last_name].filter(Boolean).join(" ") || "-",
              enrollment_date: row.start_date,
              next_schedule: nextSchedule,
              status: row.status,
            };
          } catch {
            return {
              id: row.id,
              patient_id: row.patient?.id,
              patient_name: row.patient?.name || [row.patient?.first_name, row.patient?.middle_name, row.patient?.last_name].filter(Boolean).join(" ") || "-",
              enrollment_date: row.start_date,
              next_schedule: "-",
              status: row.status,
            };
          }
        })
      );

      setSubjectRows(withNext);
    } finally {
      setSubjectsLoading(false);
    }
  };

  const loadQuestionnaires = async () => {
    setLoadingQuestionnaires(true);
    try {
      const rows = await fetchQuestionnairesForSelect();
      setQuestionnaireOptions(Array.isArray(rows) ? rows : []);
    } finally {
      setLoadingQuestionnaires(false);
    }
  };

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      setLoading(true);
      try {
        await Promise.all([
          loadProjectCore(),
          loadTimelines(),
          loadDocuments(),
          loadSubjects(),
          loadProjectPeople(),
          loadQuestionnaires(),
        ]);
      } catch (error) {
        if (!cancelled) {
          setSnackbar({
            open: true,
            message: toErrorMessage(error, "Failed to load project details"),
            severity: "error",
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    if (!Number.isNaN(projectId)) run();

    return () => {
      cancelled = true;
    };
  }, [projectId]);

  useEffect(() => {
    if (loading || !project || incompletePromptSeen) return;
    const expected = Number(project?.number_of_visits || 0);
    const actual = Number((timelineRows || []).length);
    if (expected > 0 && actual < expected) {
      setIncompletePromptOpen(true);
      setIncompletePromptSeen(true);
    }
  }, [loading, project, timelineRows, incompletePromptSeen]);

  const openTimelineModal = (row) => {
    const rowType = String(row.type || "static").toLowerCase();
    setSelectedTimeline(row);
    setTimelineForm({
      type: rowType,
      date: row.date || "",
      tolerance: Number(row.tolerance || 0),
      offset: rowType === "dynamic" ? Number(row.offset || 1) : 0,
      questionnaireQids: (row.questionnaires || []).map((q) => q.qid).filter(Boolean),
    });
    setTimelineModalOpen(true);
  };

  const getStaticDateBounds = (position, rows = [], excludeTimelineId = null) => {
    const startDate = project?.start_date || "";
    const endDate = project?.end_date || "";
    const orderedRows = [...(rows || [])]
      .filter((row) => row && Number(row.position) > 0 && row.id !== excludeTimelineId)
      .sort((a, b) => Number(a.position) - Number(b.position));

    let previousStaticDate = "";
    let dynamicOffsetSincePreviousStatic = 0;
    let nextStaticDate = "";

    orderedRows.forEach((row) => {
      const rowPosition = Number(row.position || 0);
      const rowType = String(row.type || "").toLowerCase();

      if (rowPosition < Number(position || 0)) {
        if (rowType === "static" && row.date) {
          previousStaticDate = row.date;
          dynamicOffsetSincePreviousStatic = 0;
        } else if (rowType === "dynamic") {
          const dynamicOffset = Number(row.offset || 0);
          dynamicOffsetSincePreviousStatic += Number.isFinite(dynamicOffset) ? Math.max(dynamicOffset, 0) : 0;
        }
      } else if (!nextStaticDate && rowPosition > Number(position || 0) && rowType === "static" && row.date) {
        nextStaticDate = row.date;
      }
    });

    let minDate = startDate;
    if (previousStaticDate) {
      const offsetMinDate = addDaysToIsoDate(previousStaticDate, dynamicOffsetSincePreviousStatic);
      if (!minDate || (offsetMinDate && offsetMinDate > minDate)) {
        minDate = offsetMinDate;
      }
    }
    const maxDate = nextStaticDate || endDate;

    return { minDate, maxDate };
  };

  const timelineStaticDateBounds = useMemo(() => {
    if (!selectedTimeline?.position) {
      return { minDate: "", maxDate: "" };
    }
    return getStaticDateBounds(selectedTimeline.position, timelineRows, selectedTimeline.id);
  }, [selectedTimeline?.id, selectedTimeline?.position, timelineRows, project?.start_date, project?.end_date]);

  const handleSaveTimeline = async () => {
    if (!selectedTimeline?.id) return;

    if (timelineForm.type === "dynamic") {
      const tolerance = Number(timelineForm.tolerance);
      const offset = Number(timelineForm.offset);
      if (!Number.isInteger(tolerance) || tolerance < 0) {
        setSnackbar({ open: true, message: "Tolerance must be at least 0 for dynamic visit", severity: "warning" });
        return;
      }
      if (!Number.isInteger(offset) || offset < 1) {
        setSnackbar({ open: true, message: "Offset must be at least 1 for dynamic visit", severity: "warning" });
        return;
      }
    }

    if (Number(selectedTimeline?.position) === 1 && timelineForm.type !== "static") {
      setSnackbar({
        open: true,
        message: "Schedule 1 must remain static",
        severity: "warning",
      });
      return;
    }

    const payload =
      timelineForm.type === "static"
        ? {
            type: "static",
            date: timelineForm.date || null,
            tolerance: 0,
            offset: 0,
            questionnaires: (timelineForm.questionnaireQids || []).map((qid, index) => ({
              qid,
              priority: index + 1,
            })),
          }
        : {
            type: "dynamic",
            date: null,
            tolerance: Number(timelineForm.tolerance),
            offset: Number(timelineForm.offset),
            questionnaires: (timelineForm.questionnaireQids || []).map((qid, index) => ({
              qid,
              priority: index + 1,
            })),
          };

    setTimelineSaving(true);
    try {
      await updateProjectTimeline(projectId, selectedTimeline.id, payload);
      setTimelineModalOpen(false);
      setSnackbar({ open: true, message: "Timeline updated", severity: "success" });
      await loadTimelines();
    } catch (error) {
      setSnackbar({
        open: true,
        message: toErrorMessage(error, "Failed to update timeline"),
        severity: "error",
      });
    } finally {
      setTimelineSaving(false);
    }
  };

  const missingTimelineCount = useMemo(() => {
    const expected = Number(project?.number_of_visits || 0);
    const actual = Number((timelineRows || []).length);
    return expected > actual ? expected - actual : 0;
  }, [project?.number_of_visits, timelineRows]);
  const hasIncompleteTimelines = missingTimelineCount > 0;

  const buildIncompleteScheduleRows = () => {
    const expected = Number(project?.number_of_visits || 0);
    const existingCount = Number((timelineRows || []).length);
    if (expected <= existingCount) return [];
    const rows = [];
    for (let position = existingCount + 1; position <= expected; position += 1) {
      rows.push({
        position,
        type: "static",
        date: "",
        tolerance: 0,
        offset: 0,
      });
    }
    return rows;
  };

  const getDateBoundsForIncompletePosition = (position, rows) =>
    getStaticDateBounds(position, [...(timelineRows || []), ...(rows || [])], null);

  const handleIncompleteScheduleChange = (position, field, value) => {
    setIncompleteScheduleRows((prev) =>
      prev.map((row) => {
        if (row.position !== position) return row;
        if (field === "type") {
          if (value === "static") return { ...row, type: "static", tolerance: 0, offset: 0 };
          return {
            ...row,
            type: "dynamic",
            date: "",
            tolerance: Number(row.tolerance) >= 0 ? Number(row.tolerance) : 0,
            offset: Number(row.offset) >= 1 ? Number(row.offset) : 1,
          };
        }
        return { ...row, [field]: value };
      })
    );
  };

  const handleSubmitIncompleteSchedules = async () => {
    if (!incompleteScheduleRows.length) {
      setAddSchedulesOpen(false);
      return;
    }

    const sorted = [...incompleteScheduleRows].sort((a, b) => Number(a.position) - Number(b.position));
    for (const row of sorted) {
      if (row.type === "static") {
        if (row.date && !canRelaxTimelineDateBounds) {
          const { minDate, maxDate } = getDateBoundsForIncompletePosition(row.position, sorted);
          if (minDate && row.date < minDate) {
            setSnackbar({
              open: true,
              message: `Schedule ${row.position}: date must be on or after ${minDate}`,
              severity: "warning",
            });
            return;
          }
          if (maxDate && row.date > maxDate) {
            setSnackbar({
              open: true,
              message: `Schedule ${row.position}: date cannot be after ${maxDate}`,
              severity: "warning",
            });
            return;
          }
        }
      } else {
        const tolerance = Number(row.tolerance);
        const offset = Number(row.offset);
        if (!Number.isInteger(tolerance) || tolerance < 0) {
          setSnackbar({
            open: true,
            message: `Schedule ${row.position}: dynamic tolerance must be at least 0`,
            severity: "warning",
          });
          return;
        }
        if (!Number.isInteger(offset) || offset < 1) {
          setSnackbar({
            open: true,
            message: `Schedule ${row.position}: dynamic offset must be at least 1`,
            severity: "warning",
          });
          return;
        }
      }
    }

    setAddingSchedules(true);
    try {
      await addProjectTimelinesBulk(projectId, {
        timelines: sorted.map((row) =>
          row.type === "static"
            ? { type: "static", tolerance: 0, offset: 0, date: row.date || null }
            : {
                type: "dynamic",
                tolerance: Number(row.tolerance),
                offset: Number(row.offset),
                date: null,
              }
        ),
      });
      await Promise.all([loadTimelines(), loadProjectCore()]);
      setAddSchedulesOpen(false);
      setIncompletePromptOpen(false);
      setSnackbar({ open: true, message: "Incomplete schedules added successfully", severity: "success" });
    } catch (error) {
      setSnackbar({
        open: true,
        message: toErrorMessage(error, "Failed to add incomplete schedules"),
        severity: "error",
      });
    } finally {
      setAddingSchedules(false);
    }
  };

  const handleUploadDocument = async () => {
    if (hasIncompleteTimelines) {
      setSnackbar({
        open: true,
        message: "Complete pending timelines first. This page is read-only until project is configured.",
        severity: "warning",
      });
      return;
    }
    if (!uploadForm.file || !uploadForm.document_name.trim()) {
      setSnackbar({ open: true, message: "Document name and file are required", severity: "warning" });
      return;
    }
    if (uploadForm.doctype === "Others" && !uploadForm.customDoctype.trim()) {
      setSnackbar({
        open: true,
        message: "Enter a custom document type name for Others",
        severity: "warning",
      });
      return;
    }

    const resolvedDoctype =
      uploadForm.doctype === "Others" ? uploadForm.customDoctype.trim() : uploadForm.doctype;

    setUploading(true);
    try {
      await uploadProjectDocument(projectId, {
        doctype: resolvedDoctype,
        document_name: uploadForm.document_name.trim(),
        file: uploadForm.file,
      });
      setUploadOpen(false);
      setUploadForm(emptyUploadForm);
      setSnackbar({ open: true, message: "Document uploaded", severity: "success" });
      await loadDocuments();
    } catch (error) {
      setSnackbar({
        open: true,
        message: toErrorMessage(error, "Failed to upload document"),
        severity: "error",
      });
    } finally {
      setUploading(false);
    }
  };

  const handleUploadChange = (event) => {
    const { name, value } = event.target;
    setUploadForm((prev) => ({ ...prev, [name]: value }));
  };

  const setUploadFile = (file) => {
    if (!file) return;
    setUploadForm((prev) => ({
      ...prev,
      file,
      document_name: prev.document_name || file.name,
    }));
  };

  const handleFileInput = (event) => {
    const file = event.target.files?.[0];
    setUploadFile(file);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);
    const file = event.dataTransfer?.files?.[0];
    setUploadFile(file);
  };

  const handleDownloadDocument = async (row) => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(getProjectDocumentDownloadUrl(projectId, row.id), {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || "Failed to download document");
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = row.filename || "document";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setSnackbar({
        open: true,
        message: toErrorMessage(error, "Failed to download document"),
        severity: "error",
      });
    }
  };

  const handleDeleteDocument = async () => {
    if (hasIncompleteTimelines) {
      setSnackbar({
        open: true,
        message: "Complete pending timelines first. This page is read-only until project is configured.",
        severity: "warning",
      });
      return;
    }
    if (!deleteDocumentId) return;
    try {
      await deleteProjectDocument(projectId, deleteDocumentId);
      setDeleteDocumentId(null);
      setSnackbar({ open: true, message: "Document deleted", severity: "success" });
      await loadDocuments();
    } catch (error) {
      setDeleteDocumentId(null);
      setSnackbar({
        open: true,
        message: toErrorMessage(error, "Failed to delete document"),
        severity: "error",
      });
    }
  };

  const canEditProjectInfo = useMemo(() => {
    const hasSubjectsLocal = Array.isArray(subjectRows) && subjectRows.length > 0;
    // If there are enrolled subjects, prevent changing core project setup
    // while timelines are incomplete. If no subjects, allow editing so
    // schedules can be configured before enrollment starts.
    if (hasIncompleteTimelines && hasSubjectsLocal) return false;
    if (isAdmin) return true;
    if (isManager) {
      return Boolean(username) && String(project?.client_username || "") === String(username);
    }
    return false;
  }, [hasIncompleteTimelines, subjectRows, isAdmin, isManager, project?.client_username, username]);

  const canCreatePatient = useMemo(() => {
    if (hasIncompleteTimelines) return false;
    if (isAdmin) return true;
    if (isManager) {
      return Boolean(username) && String(project?.client_username || "") === String(username);
    }
    return false;
  }, [hasIncompleteTimelines, isAdmin, isManager, project?.client_username, username]);

  const handleProjectFormChange = (event) => {
    if (!canEditProjectInfo) return;
    const { name, value } = event.target;
    if (name === "number_of_visits") {
      setProjectForm((prev) => ({
        ...prev,
        number_of_visits: value,
      }));
    } else {
      setProjectForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleProjectReset = () => {
    if (!canEditProjectInfo || !initialProjectForm) return;
    setProjectForm(initialProjectForm);
    setProjectPeople(initialProjectPeople);
    setScheduleChangePrompt({ open: false, mode: null });
  };

  const runProjectSave = async () => {
    setSavingProject(true);
    try {
      const prevNumberOfVisits =
        initialNumberOfVisits != null
          ? Number(initialNumberOfVisits)
          : Number(project?.number_of_visits || 0);
      const nextNumberOfVisits = Number(
        projectForm.number_of_visits !== undefined && projectForm.number_of_visits !== null
          ? projectForm.number_of_visits
          : prevNumberOfVisits
      );

      await updateProject(projectId, {
        name: projectForm.name,
        status: projectForm.status,
        type: projectForm.type,
        number_of_visits: nextNumberOfVisits || prevNumberOfVisits,
        start_date: projectForm.start_date || null,
        end_date: projectForm.end_date || null,
      });

      const pid = project?.pid;
      if (pid) {
        const currentUsernames = new Set((initialProjectPeople || []).map((u) => u.username).filter(Boolean));
        const nextUsernames = new Set((projectPeople || []).map((u) => u.username).filter(Boolean));

        const usernamesToAdd = [...nextUsernames].filter((username) => !currentUsernames.has(username));
        const usernamesToRemove = [...currentUsernames].filter((username) => !nextUsernames.has(username));

        if (usernamesToAdd.length > 0) {
          await Promise.allSettled(
            usernamesToAdd.map((username) =>
              addProjectUser({
                pid,
                username,
              })
            )
          );
        }

        if (usernamesToRemove.length > 0) {
          await Promise.allSettled(
            usernamesToRemove.map((username) => deleteProjectUser(pid, username))
          );
        }
      }

      setSnackbar({ open: true, message: "Project updated successfully", severity: "success" });
      // Reload all core data so timelines and counts reflect any deletions/additions
      await Promise.all([loadProjectCore(), loadProjectPeople(), loadTimelines(), loadSubjects(), loadDocuments()]);
    } catch (error) {
      setSnackbar({
        open: true,
        message: toErrorMessage(error, "Failed to update project"),
        severity: "error",
      });
    } finally {
      setSavingProject(false);
    }
  };

  const handleProjectSave = () => {
    if (!canEditProjectInfo) return;

    const prevNumberOfVisits =
      initialNumberOfVisits != null
        ? Number(initialNumberOfVisits)
        : Number(project?.number_of_visits || 0);
    const nextNumberOfVisits = Number(
      projectForm.number_of_visits !== undefined && projectForm.number_of_visits !== null
        ? projectForm.number_of_visits
        : prevNumberOfVisits
    );

    // If user is decreasing schedules, confirm before saving (this will delete last schedules).
    if (nextNumberOfVisits < prevNumberOfVisits) {
      setScheduleChangePrompt({ open: true, mode: "decrease" });
      return;
    }

    // No change or increase: save immediately; increase prompt handled after save in runProjectSave.
    runProjectSave();
  };

  const timelineColumns = [
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      sortable: false,
      filterable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Tooltip title="Edit Timeline">
          <IconButton
            size="small"
            color="primary"
            onClick={() => openTimelineModal(params.row)}
            disabled={hasIncompleteTimelines}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
    { field: "position", headerName: "Serial", width: 90 },
    { field: "type", headerName: "Type", minWidth: 120, flex: 0.7, renderCell: (p) => toTitleCase(p.value) },
    {
      field: "date",
      headerName: "Date",
      minWidth: 130,
      flex: 0.8,
      renderCell: (params) => formatDate(params.value),
    },
    { field: "offset", headerName: "Offset", minWidth: 100, flex: 0.6 },
    { field: "tolerance", headerName: "Tolerance", minWidth: 120, flex: 0.7 },
  ];

  const documentColumns = [
    {
      field: "actions",
      headerName: "Actions",
      sortable: false,
      filterable: false,
      minWidth: 120,
      flex: 0.7,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Tooltip title="Download">
            <IconButton size="small" color="primary" onClick={() => handleDownloadDocument(params.row)}>
              <DownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {isAdmin ? (
            <Tooltip title="Delete">
              <IconButton
                size="small"
                color="error"
                onClick={() => setDeleteDocumentId(params.row.id)}
                disabled={hasIncompleteTimelines}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : null}
        </Box>
      ),
    },
    { field: "document_name", headerName: "Document Name", minWidth: 180, flex: 1 },
    { field: "doctype", headerName: "Document Type", minWidth: 140, flex: 0.8 },
    { field: "filename", headerName: "Filename", minWidth: 180, flex: 1 },
    {
      field: "byte_size",
      headerName: "Size",
      minWidth: 100,
      flex: 0.6,
      renderCell: (params) => formatBytes(params.value),
    },
    {
      field: "upload_date",
      headerName: "Upload Date",
      minWidth: 140,
      flex: 0.8,
      renderCell: (params) => formatDate(params.value),
    },
  ];

  const subjectColumns = [
    {
      field: "action",
      headerName: "Action",
      minWidth: 120,
      flex: 0.6,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Button
          size="small"
          variant="outlined"
          onClick={() => navigate(`/subject-management/edit/${params.row.patient_id}`)}
          disabled={!params.row.patient_id}
        >
          Edit
        </Button>
      ),
    },
    {
      field: "patient_name",
      headerName: "Subject Name",
      minWidth: 180,
      flex: 1.2,
      renderCell: (params) => (
        <DatagridNavLink
          to={params.row.patient_id ? `/subject-management/edit/${params.row.patient_id}` : ""}
        >
          {params.value}
        </DatagridNavLink>
      ),
    },
    {
      field: "enrollment_date",
      headerName: "Enrollment Date",
      minWidth: 150,
      flex: 0.8,
      renderCell: (params) => formatDate(params.value),
    },
    { field: "next_schedule", headerName: "Next Schedule", minWidth: 160, flex: 0.9 },
    {
      field: "status",
      headerName: "Status",
      minWidth: 120,
      flex: 0.7,
      renderCell: (params) => toTitleCase(params.value),
    },
  ];

  const projectHeader = useMemo(() => {
    if (!project) return "Project / Details";
    return `Project / ${project.name || project.pid || "Details"}`;
  }, [project]);

  return (
    <Box sx={{ bgcolor: "#f5f5f5", minHeight: "100vh" }}>
      <PageBanner title={projectHeader} />

      <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3 }, pb: 4 }}>
        <Box sx={{ mt: 3, mb: 3 }}>
          <Button startIcon={<ArrowBackIcon />} variant="text" onClick={() => navigate("/project")}>Back to Project List</Button>
        </Box>

        {loading ? (
          <Paper elevation={3} sx={{ p: 4, borderRadius: 3 }}>
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress />
            </Box>
          </Paper>
        ) : (
          <Box
            sx={{
              mb: 4,
              display: "grid",
              gap: 3,
              gridTemplateColumns: {
                xs: "1fr",
                md: "minmax(520px, 62%) minmax(360px, 38%)",
              },
              alignItems: "start",
              height: { xs: "auto", md: "calc(100vh - 250px)" },
              overflow: { xs: "visible", md: "hidden" },
            }}
          >
            <Paper
              elevation={3}
              sx={{
                p: 3,
                borderRadius: 3,
                height: { xs: "auto", md: "100%" },
                overflowY: { xs: "visible", md: "auto" },
                scrollbarWidth: "none",
                msOverflowStyle: "none",
                "&::-webkit-scrollbar": { display: "none" },
              }}
            >
              <Typography variant="h6" fontWeight={700} mb={2}>
                Project Information
              </Typography>

              <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700 }}>
                Basic Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField fullWidth label="Project ID" value={project?.pid || ""} disabled />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    name="name"
                    label="Project Name"
                    value={projectForm.name}
                    onChange={handleProjectFormChange}
                    disabled={!canEditProjectInfo}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth label="Client Name" value={project?.client_name || "-"} disabled />
                </Grid>
                <Grid item xs={12} sx={{ display: "flex" }}>
                  <Autocomplete
                    fullWidth
                    sx={{ flexGrow: 1 }}
                    multiple
                    options={keycloakUsers}
                    loading={loadingProjectPeople}
                    value={projectPeople}
                    getOptionLabel={(option) => {
                      const uname = option?.username || "";
                      const display = option?.name || option?.full_name || "";
                      return display ? `${display} (${uname})` : uname;
                    }}
                    onChange={(_, value) => {
                      if (!canEditProjectInfo) return;
                      setProjectPeople(value || []);
                    }}
                    isOptionEqualToValue={(option, value) => option.username === value.username}
                    disabled={!canEditProjectInfo}
                    renderInput={(params) => <TextField {...params} label="Project Person" fullWidth />}
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700 }}>
                Project Configuration
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    select
                    name="status"
                    label="Status"
                    value={projectForm.status}
                    onChange={handleProjectFormChange}
                    disabled={!canEditProjectInfo}
                  >
                    {["pending", "active", "completed", "submitted"].map((option) => (
                      <MenuItem key={option} value={option}>
                        {toTitleCase(option)}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    select
                    name="type"
                    label="Type"
                    value={projectForm.type}
                    onChange={handleProjectFormChange}
                    disabled={!canEditProjectInfo}
                  >
                    {["internal", "external", "research", "other"].map((option) => (
                      <MenuItem key={option} value={option}>
                        {toTitleCase(option)}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    type="number"
                    name="number_of_visits"
                    label="Number of Schedules"
                    inputProps={{ min: 1 }}
                    value={projectForm.number_of_visits}
                    onChange={handleProjectFormChange}
                    disabled={
                      !canEditProjectInfo ||
                      (Array.isArray(subjectRows) && subjectRows.length > 0)
                    }
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700 }}>
                Project Dates
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <DatePicker
                    label="Start Date"
                    format="MM/DD/YYYY"
                    value={projectForm.start_date ? dayjs(projectForm.start_date) : null}
                    onChange={(value) => {
                      setProjectForm((prev) => ({
                        ...prev,
                        start_date: value && value.isValid() ? value.format("YYYY-MM-DD") : "",
                      }));
                    }}
                    disabled={
                      !canEditProjectInfo ||
                      (Array.isArray(subjectRows) && subjectRows.length > 0)
                    }
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        name: "start_date",
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <DatePicker
                    label="End Date"
                    format="MM/DD/YYYY"
                    value={projectForm.end_date ? dayjs(projectForm.end_date) : null}
                    onChange={(value) => {
                      setProjectForm((prev) => ({
                        ...prev,
                        end_date: value && value.isValid() ? value.format("YYYY-MM-DD") : "",
                      }));
                    }}
                    disabled={
                      !canEditProjectInfo ||
                      (Array.isArray(subjectRows) && subjectRows.length > 0)
                    }
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        name: "end_date",
                      },
                    }}
                  />
                </Grid>
              </Grid>

              <Box sx={{ mt: 3, display: "flex", gap: 2, justifyContent: "flex-end" }}>
                {canEditProjectInfo ? (
                  <>
                    <Button variant="outlined" onClick={handleProjectReset} disabled={savingProject}>
                      Reset
                    </Button>
                    <Button variant="contained" onClick={handleProjectSave} disabled={savingProject}>
                      {savingProject ? "Saving..." : "Update Project"}
                    </Button>
                  </>
                ) : null}
              </Box>
            </Paper>

            <Box
              sx={{
                height: { xs: "auto", md: "100%" },
                overflowY: { xs: "visible", md: "auto" },
                pr: { md: 0.5 },
                scrollbarWidth: "none",
                msOverflowStyle: "none",
                "&::-webkit-scrollbar": { display: "none" },
              }}
            >
              <Paper elevation={3} sx={{ p: 3, borderRadius: 3, mb: 3 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, gap: 1, flexWrap: "wrap" }}>
                  <Typography variant="h6" fontWeight={700}>
                    Subjects
                  </Typography>
                  {canCreatePatient ? (
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => setCreatePatientOpen(true)}
                    >
                      Create Subject
                    </Button>
                  ) : null}
                </Box>
                <DataGrid
                  rows={subjectRows}
                  columns={subjectColumns}
                  loading={subjectsLoading}
                  autoHeight
                  hideFooter
                  disableRowSelectionOnClick
                  sx={{
                    "& .MuiDataGrid-row": {
                      backgroundColor: "#ffffff",
                    },
                    "& .MuiDataGrid-row:hover": {
                      backgroundColor: "#ffffff",
                    },
                    "& .MuiDataGrid-row.Mui-selected": {
                      backgroundColor: "#ffffff",
                    },
                    "& .MuiDataGrid-row.Mui-selected:hover": {
                      backgroundColor: "#ffffff",
                    },
                  }}
                />
              </Paper>

              <Paper elevation={3} sx={{ p: 3, borderRadius: 3, mb: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                  <Typography variant="h6" fontWeight={700}>
                    Timelines
                  </Typography>
                </Box>
                <DataGrid
                  rows={timelineRows}
                  columns={timelineColumns}
                  loading={timelinesLoading}
                  autoHeight
                  hideFooter
                  disableRowSelectionOnClick
                  sx={{
                    "& .MuiDataGrid-row": {
                      backgroundColor: "#ffffff",
                    },
                    "& .MuiDataGrid-row:hover": {
                      backgroundColor: "#ffffff",
                    },
                    "& .MuiDataGrid-row.Mui-selected": {
                      backgroundColor: "#ffffff",
                    },
                    "& .MuiDataGrid-row.Mui-selected:hover": {
                      backgroundColor: "#ffffff",
                    },
                  }}
                />
              </Paper>

              <Paper elevation={3} sx={{ p: 3, borderRadius: 3, mb: 3 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                  <Typography variant="h6" fontWeight={700}>
                    Project Documents
                  </Typography>
                  {isAdmin ? (
                    <Button
                      variant="contained"
                      onClick={() => {
                        setUploadForm(emptyUploadForm);
                        setUploadOpen(true);
                      }}
                      disabled={hasIncompleteTimelines}
                    >
                      Add Document
                    </Button>
                  ) : null}
                </Box>
                <DataGrid
                  rows={documentRows}
                  columns={documentColumns}
                  loading={documentsLoading}
                  autoHeight
                  hideFooter
                  disableRowSelectionOnClick
                  sx={{
                    "& .MuiDataGrid-row": {
                      backgroundColor: "#ffffff",
                    },
                    "& .MuiDataGrid-row:hover": {
                      backgroundColor: "#ffffff",
                    },
                    "& .MuiDataGrid-row.Mui-selected": {
                      backgroundColor: "#ffffff",
                    },
                    "& .MuiDataGrid-row.Mui-selected:hover": {
                      backgroundColor: "#ffffff",
                    },
                  }}
                />
              </Paper>
            </Box>
          </Box>
        )}
      </Container>

      <Dialog
        open={timelineModalOpen}
        onClose={(_, reason) => {
          if (reason === "backdropClick") return;
          setTimelineModalOpen(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pr: 6 }}>
          Edit Timeline
          <IconButton
            aria-label="close"
            onClick={() => setTimelineModalOpen(false)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: "grid", gap: 2 }}>
            <TextField fullWidth label="Serial Number" value={selectedTimeline?.position || ""} disabled />
            <TextField
              fullWidth
              select
              label="Type of Visit"
              value={timelineForm.type}
              disabled={Number(selectedTimeline?.position) === 1}
              onChange={(event) => {
                const nextType = event.target.value;
                setTimelineForm((prev) => ({
                  ...prev,
                  type: nextType,
                  date: nextType === "dynamic" ? "" : prev.date,
                  tolerance: nextType === "static" ? 0 : Math.max(Number(prev.tolerance || 0), 0),
                  offset: nextType === "static" ? 0 : Math.max(Number(prev.offset || 1), 1),
                }));
              }}
            >
              <MenuItem value="static">Static</MenuItem>
              <MenuItem value="dynamic">Dynamic</MenuItem>
            </TextField>
            <TextField
              fullWidth
              type="number"
              label="Offset (days)"
              value={timelineForm.type === "static" ? 0 : timelineForm.offset}
              onChange={(event) => setTimelineForm((prev) => ({ ...prev, offset: event.target.value }))}
              disabled={timelineForm.type === "static"}
              inputProps={{ min: 1 }}
              helperText={timelineForm.type === "static" ? "Static visit keeps offset as 0" : "Minimum 1"}
            />
            <TextField
              fullWidth
              type="number"
              label="Tolerance Buffer (days)"
              value={timelineForm.type === "static" ? 0 : timelineForm.tolerance}
              onChange={(event) => setTimelineForm((prev) => ({ ...prev, tolerance: event.target.value }))}
              disabled={timelineForm.type === "static"}
              inputProps={{ min: 0 }}
              helperText={timelineForm.type === "static" ? "Static visit keeps tolerance as 0" : "Minimum 0"}
            />
            {timelineForm.type === "static" ? (
              <DatePicker
                label="Static visit date"
                format="MM/DD/YYYY"
                value={timelineForm.date ? dayjs(timelineForm.date) : null}
                onChange={(value) => {
                  setTimelineForm((prev) => ({
                    ...prev,
                    date: value && value.isValid() ? value.format("YYYY-MM-DD") : "",
                  }));
                }}
                minDate={
                  canRelaxTimelineDateBounds || !timelineStaticDateBounds.minDate
                    ? undefined
                    : dayjs(timelineStaticDateBounds.minDate)
                }
                maxDate={
                  canRelaxTimelineDateBounds || !timelineStaticDateBounds.maxDate
                    ? undefined
                    : dayjs(timelineStaticDateBounds.maxDate)
                }
                slotProps={{
                  textField: {
                    fullWidth: true,
                    helperText: canRelaxTimelineDateBounds
                      ? "Admin/Manager: any date allowed (including backdates)."
                      : "Must fall within the project window relative to other static visits.",
                  },
                }}
              />
            ) : null}
            <Autocomplete
              multiple
              options={questionnaireOptions}
              loading={loadingQuestionnaires}
              value={questionnaireOptions.filter((option) =>
                (timelineForm.questionnaireQids || []).includes(option.qid)
              )}
              getOptionLabel={(option) => {
                return option?.label
                  ? `${option.label} (${option.qid})`
                  : `${option?.name || option?.qid} (${option?.qid})`;
              }}
              isOptionEqualToValue={(option, value) => option.qid === value.qid}
              onChange={(_, value) =>
                setTimelineForm((prev) => ({
                  ...prev,
                  questionnaireQids: (value || []).map((item) => item.qid).filter(Boolean),
                }))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Map Questionnaires"
                  placeholder="Select questionnaire(s)"
                  helperText="Multi-select questionnaires mapped to this timeline"
                />
              )}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTimelineModalOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveTimeline} variant="contained" disabled={timelineSaving}>
            {timelineSaving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={uploadOpen}
        onClose={(_, reason) => {
          if (reason === "backdropClick") return;
          setUploadOpen(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pr: 6 }}>
          Add Project Document
          <IconButton
            aria-label="close"
            onClick={() => setUploadOpen(false)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: "grid", gap: 2 }}>
            <TextField
              select
              name="doctype"
              label="Document Type"
              value={uploadForm.doctype}
              onChange={handleUploadChange}
              fullWidth
            >
              {doctypeOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>

            {uploadForm.doctype === "Others" ? (
              <TextField
                name="customDoctype"
                label="Custom Document Type"
                value={uploadForm.customDoctype}
                onChange={handleUploadChange}
                fullWidth
                required
                placeholder="Enter document type name"
                helperText="Required when Document Type is Others"
              />
            ) : null}

            <TextField
              name="document_name"
              label="Document Name"
              value={uploadForm.document_name}
              onChange={handleUploadChange}
              fullWidth
            />

            <Box
              onDragEnter={(event) => {
                event.preventDefault();
                setDragActive(true);
              }}
              onDragOver={(event) => {
                event.preventDefault();
                setDragActive(true);
              }}
              onDragLeave={(event) => {
                event.preventDefault();
                setDragActive(false);
              }}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              sx={{
                border: "1px dashed #9aa4b2",
                borderRadius: 2,
                p: 3,
                textAlign: "center",
                cursor: "pointer",
                bgcolor: dragActive ? "#eef4ff" : "transparent",
              }}
            >
              <Typography variant="body2" sx={{ mb: 1 }}>
                Drag and drop file here, or click to select
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {uploadForm.file ? `Selected: ${uploadForm.file.name}` : "No file selected"}
              </Typography>
              <input
                ref={fileInputRef}
                type="file"
                style={{ display: "none" }}
                onChange={handleFileInput}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadOpen(false)}>Cancel</Button>
          <Button onClick={handleUploadDocument} variant="contained" disabled={uploading || hasIncompleteTimelines}>
            {uploading ? "Uploading..." : "Upload"}
          </Button>
        </DialogActions>
      </Dialog>

      <AppConfirmDialog
        open={Boolean(deleteDocumentId)}
        title="Delete Document"
        content="Are you sure you want to delete this document?"
        confirmLabel="Delete"
        confirmColor="error"
        onCancel={() => setDeleteDocumentId(null)}
        onConfirm={handleDeleteDocument}
      />

      <Dialog
        open={incompletePromptOpen}
        onClose={(_, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          setIncompletePromptOpen(false);
        }}
        disableEscapeKeyDown
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Timeline Setup Pending</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This project is not configured yet. {missingTimelineCount} schedule(s) are still pending.
            Please add remaining timelines before continuing further.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => navigate("/project")} color="inherit">
            Back to Projects
          </Button>
          <Button
            onClick={() => {
              setIncompleteScheduleRows(buildIncompleteScheduleRows());
              setIncompletePromptOpen(false);
              setAddSchedulesOpen(true);
            }}
            variant="contained"
          >
            Add Incomplete Schedules
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={addSchedulesOpen}
        onClose={(_, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          setAddSchedulesOpen(false);
        }}
        disableEscapeKeyDown
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Incomplete Schedules</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: "grid", gap: 2 }}>
            {(incompleteScheduleRows || []).map((row) => (
                <Box
                  key={row.position}
                  sx={{
                    p: 2,
                    border: "1px solid #d7dbe8",
                    borderRadius: 1,
                    bgcolor: "#ffffff",
                  }}
                >
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField fullWidth label="Serial Number" value={row.position} disabled />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        select
                        label="Type of Visit"
                        value={row.type}
                        onChange={(event) => handleIncompleteScheduleChange(row.position, "type", event.target.value)}
                      >
                        <MenuItem value="static">Static</MenuItem>
                        <MenuItem value="dynamic">Dynamic</MenuItem>
                      </TextField>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Offset (days)"
                        value={row.type === "static" ? 0 : row.offset}
                        onChange={(event) => handleIncompleteScheduleChange(row.position, "offset", event.target.value)}
                        disabled={row.type === "static"}
                        inputProps={{ min: 1 }}
                        helperText={row.type === "static" ? "Static visit keeps offset as 0" : "Minimum 1"}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Tolerance Buffer (days)"
                        value={row.type === "static" ? 0 : row.tolerance}
                        onChange={(event) => handleIncompleteScheduleChange(row.position, "tolerance", event.target.value)}
                        disabled={row.type === "static"}
                        inputProps={{ min: 0 }}
                        helperText={row.type === "static" ? "Static visit keeps tolerance as 0" : "Minimum 0"}
                      />
                    </Grid>
                  </Grid>
                </Box>
              ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setAddSchedulesOpen(false);
            }}
            disabled={addingSchedules}
          >
            Cancel
          </Button>
          <Button onClick={handleSubmitIncompleteSchedules} variant="contained" disabled={addingSchedules}>
            {addingSchedules ? "Submitting..." : "Add Schedules"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Prompt for schedule count changes when no associated patients */}
      <AppConfirmDialog
        open={scheduleChangePrompt.open && scheduleChangePrompt.mode === "decrease"}
        title="Reduce number of schedules?"
        content="You have decreased the number of schedules. This will delete the last schedules in the project timeline. Do you want to continue?"
        confirmLabel="Yes, delete last schedules"
        cancelLabel="Cancel"
        onCancel={() => setScheduleChangePrompt({ open: false, mode: null })}
        onConfirm={() => {
          setScheduleChangePrompt({ open: false, mode: null });
          runProjectSave();
        }}
      />

      <PatientCreateDialog
        open={createPatientOpen}
        onClose={() => setCreatePatientOpen(false)}
        presetClientId={project?.cid || ""}
        presetProjectId={projectId}
        lockClientProject
        onCreated={async () => {
          setSnackbar({
            open: true,
            message: "Subject created successfully.",
            severity: "success",
          });
          await loadSubjects();
        }}
      />

      <AppToast
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      />
    </Box>
  );
};

export default ProjectDetail;
